import { Directive, ElementRef, inject, input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  switchMap,
  map,
  interval,
  takeWhile,
  endWith,
  distinctUntilChanged,
  takeUntil,
  ReplaySubject
} from 'rxjs';

const easeOutQuad = (x: number): number => x * (2 - x);

@Directive({
  standalone: true,
  selector: '[countUp]'
})
export class CountUpDirective implements OnInit, OnDestroy, OnChanges {
  countUp = input.required<number>();
  duration = input.required<number>();

  private readonly count$ = new BehaviorSubject(0);
  private readonly duration$ = new BehaviorSubject(2000);
  private readonly destroy$ = new ReplaySubject<void>(1);

  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private readonly currentCount$ = combineLatest([this.count$, this.duration$]).pipe(
    switchMap(([count, duration]) => {
      const startTime = animationFrameScheduler.now();

      return interval(0, animationFrameScheduler).pipe(
        map(() => animationFrameScheduler.now() - startTime),
        map((elapsedTime) => elapsedTime / duration),
        takeWhile((progress) => progress <= 1),
        map(easeOutQuad),
        map((progress) => Math.round(progress * count)),
        endWith(count),
        distinctUntilChanged()
      );
    })
  );

  ngOnInit(): void {
    this.displayCurrentCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.countUp) {
      this.count$.next(changes.countUp.currentValue);
    }

    if (changes.duration) {
      this.duration$.next(changes.duration.currentValue);
    }
  }

  private displayCurrentCount(): void {
    this.currentCount$.pipe(takeUntil(this.destroy$)).subscribe((currentCount) => {
      this.renderer.setProperty(this.elementRef.nativeElement, 'innerHTML', currentCount);
    });
  }
}
